import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import QuoteBuilder from '../components/QuoteBuilder';
import BackgroundAnimation from '../components/BackgroundAnimation';

const Pricing = () => {
  const [quoteStep, setQuoteStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState({
    website: null,
    leadGen: null,
    automation: null,
    addons: []
  });

  const pricingPlans = [
    {
      name: 'Starter Bundle',
      description: 'Perfect for small businesses ready to establish their digital presence',
      price: '$1,000 - $1,500',
      period: 'per month',
      features: [
        'Professional Website Design',
        'Basic SEO Optimization',
        'Social Media Setup',
        'Email Marketing Foundation',
        'Monthly Analytics Report',
        'Standard Support'
      ],
      popular: false
    },
    {
      name: 'Growth Bundle',
      description: 'Scale across multiple channels with advanced automation',
      price: '$2,000 - $3,500',
      period: 'per month',
      features: [
        'Everything in Starter',
        'AI-Powered CRM Integration',
        'Advanced Ad Campaign Management',
        'Content Strategy & Creation',
        'Conversion Rate Optimization',
        'Weekly Strategy Sessions',
        'Priority Support',
        'Custom AI Agent Development'
      ],
      popular: true
    },
    {
      name: 'Pro Bundle',
      description: 'Full-scale AI and creative automation for aggressive growth',
      price: '$4,000 - $6,000+',
      period: 'per month',
      features: [
        'Everything in Growth',
        'Custom AI Automation Systems',
        'Multi-Channel Marketing Automation',
        'Advanced Analytics & Reporting',
        'Brand Strategy & Development',
        'Dedicated Account Manager',
        '24/7 Premium Support'
      ],
      popular: false
    },
    {
      name: 'Custom Enterprise',
      description: 'Tailored solutions for franchises and large-scale operations',
      price: 'Custom Pricing',
      period: 'on request',
      features: [
        'Fully Custom Solutions',
        'Enterprise-Grade Infrastructure',
        'White-Label Options',
        'Multi-Location Support',
        'Custom Integrations',
        'Dedicated Development Team',
        'SLA Guarantee'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      {/* Hero */}
      <section className="py-24 fade-in" style={{ padding: '120px 7.6923% 60px' }}>
        <div className="max-w-5xl mx-auto text-center">
          <div className="body-medium mb-2" style={{ color: 'var(--brand-primary)' }}>
            Investment Plans
          </div>
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Choose the Right Plan
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              for Your Growth
            </span>
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Transparent pricing designed to deliver maximum ROI at every stage
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12" style={{ padding: '60px 7.6923% 100px' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`p-8 rounded-lg dark-hover relative stagger-item ${
                  plan.popular ? 'ring-2' : ''
                }`}
                style={{
                  background: plan.popular
                    ? 'rgba(0, 255, 209, 0.05)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: `1px solid ${
                    plan.popular ? 'var(--brand-primary)' : 'var(--border-subtle)'
                  }`,
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                {plan.popular && (
                  <div
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full flex items-center space-x-1"
                    style={{
                      background: 'var(--brand-primary)',
                      color: '#000000'
                    }}
                  >
                    <Sparkles size={14} />
                    <span className="body-small font-semibold">Most Popular</span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>
                    {plan.name}
                  </h3>
                  <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {plan.description}
                  </p>
                  <div className="mb-1">
                    <span className="display-medium" style={{ color: 'var(--brand-primary)' }}>
                      {plan.price}
                    </span>
                  </div>
                  <span className="body-small" style={{ color: 'var(--text-muted)' }}>
                    {plan.period}
                  </span>
                </div>

                <div className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <CheckCircle2
                        size={18}
                        style={{ color: 'var(--brand-primary)', flexShrink: 0, marginTop: '2px' }}
                      />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href="https://calendly.com/americanprimeforyou"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button-text dark-button-animate w-full text-center inline-flex items-center justify-center"
                  style={{
                    background: plan.popular
                      ? 'var(--brand-primary)'
                      : 'rgba(255, 255, 255, 0.1)',
                    color: plan.popular ? '#000000' : '#FFFFFF',
                    padding: '12px 20px',
                    borderRadius: '0px',
                    border: 'none',
                    textDecoration: 'none',
                    fontSize: '14px'
                  }}
                >
                  Book a Free Strategy Call
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Build Your Own Quote Section */}
      <section
        className="py-24"
        style={{
          padding: '100px 7.6923%',
          background: 'rgba(0, 255, 209, 0.02)',
          borderTop: '1px solid rgba(0, 255, 209, 0.1)',
          borderBottom: '1px solid rgba(0, 255, 209, 0.1)'
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
              Build Your Own Quote
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Select the services you need and get an instant estimate
            </p>
          </div>
          <QuoteBuilder />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked
            <span style={{ color: 'var(--brand-primary)' }}> Questions</span>
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Absolutely! Plan adjustments follow our service agreement terms. Upgrades activate instantly, while downgrades process at your next renewal cycle.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept a wide range of global payment methods, including all major credit cards (Visa, MasterCard, American Express), ACH bank transfers, wire transfers, PayPal, Zelle, Venmo, and other international payment options for global clients and enterprise partnerships.'
              },
              {
                q: 'Is there a setup fee?',
                a: 'Setup investments vary based on your chosen services. Your complimentary strategy session includes a transparent cost breakdown.'
              },
              {
                q: 'How long are the contracts?',
                a: 'We provide flexible month-to-month agreements as well as long-term contract options. Services may be discontinued prior to the contract end based on agreed terms, and contracts may renew automatically unless canceled in accordance with the contract conditions.'
              },
              {
                q: 'Do you offer discounts for annual commitments?',
                a: 'Yes! Secure up to 20% savings with annual prepayment. Connect with our team to explore premium annual investment plans.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="p-6 rounded-lg"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <h3 className="heading-3 mb-3" style={{ color: 'var(--text-primary)' }}>
                  {faq.q}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;